import React, { useState } from 'react';
import { Play, ExternalLink, Loader2 } from 'lucide-react';

interface VimeoVideoPlayerProps {
  videoId: string;
  title: string;
  duration?: string;
  autoplay?: boolean;
  className?: string;
  showTitle?: boolean;
  onVideoStart?: () => void;
  onVideoEnd?: () => void;
}

export const VimeoVideoPlayer: React.FC<VimeoVideoPlayerProps> = ({
  videoId,
  title,
  duration,
  autoplay = false,
  className = '',
  showTitle = true,
  onVideoStart,
  onVideoEnd,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const vimeoUrl = `https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479${
    autoplay ? '&autoplay=1&muted=1' : ''
  }`;
  const directUrl = `https://vimeo.com/${videoId}`;

  const handleIframeLoad = () => {
    setIsLoading(false);
    if (autoplay && onVideoStart) {
      onVideoStart();
    }
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-6 text-center ${className}`}>
        <div className="text-gray-600 mb-4">
          <p className="mb-2">Unable to load video player</p>
          <p className="text-sm">You can watch this video directly on Vimeo</p>
        </div>
        <a
          href={directUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
        >
          <ExternalLink className="w-4 h-4" />
          Watch on Vimeo
        </a>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {showTitle && (
        <div className="mb-3 flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">{title}</h4>
          {duration && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Play className="w-3 h-3" />
              {duration}
            </span>
          )}
        </div>
      )}

      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="ml-2 text-gray-600">Loading video...</span>
          </div>
        )}

        <iframe
          src={vimeoUrl}
          className="absolute top-0 left-0 w-full h-full rounded-lg shadow-sm"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={title}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </div>

      <div className="mt-2 text-center">
        <a
          href={directUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" />
          Open in new tab
        </a>
      </div>
    </div>
  );
};
